import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { EXO_DRIVERS } from "@/data/driversExo";
import { API, STORAGE_KEY, ENTOURAGE_KEY, scrollTop, DEFAULT_RANKING } from "@/components/exo/driversExoShared";
import { Welcome, DriverPage, EvaluationStep } from "@/components/exo/DriversSteps";
import { Synthese } from "@/components/exo/DriversSynthese";
import { EntouragePanel } from "@/components/exo/EntouragePanel";
import { DriversEntourageInvite } from "@/components/exo/DriversEntourageInvite";

const loadSaved = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const emptyEntry = () => ({
  utile: { text: "" },
  pas_utile: { text: "" },
  aurait_utile: { text: "" },
  premierPas: "",
  commentaires: "",
});

const freshEntries = () => Object.fromEntries(EXO_DRIVERS.map((d) => [d.key, emptyEntry()]));

const hasProgress = (saved) => {
  if (!saved) return false;
  const e = saved.entries || {};
  const entriesFilled = Object.values(e).some(
    (v) =>
      v &&
      (v.commentaires || v.premierPas || ["utile", "pas_utile", "aurait_utile"].some((k) => v[k] && v[k].text))
  );
  const rankChanged = saved.ranking && saved.ranking.join(",") !== DEFAULT_RANKING.join(",");
  return entriesFilled || rankChanged;
};

export default function DriversExoModule() {
  const saved = useMemo(loadSaved, []);
  const [phase, setPhase] = useState("welcome"); // welcome | drivers | evaluation | entourage | synthese
  const [index, setIndex] = useState(0);
  const [entries, setEntries] = useState(() => {
    const base = freshEntries();
    if (saved?.entries) {
      EXO_DRIVERS.forEach((d) => {
        if (saved.entries[d.key]) base[d.key] = { ...emptyEntry(), ...saved.entries[d.key] };
      });
    }
    return base;
  });
  const [ranking, setRanking] = useState(() => {
    if (saved?.ranking && saved.ranking.length === DEFAULT_RANKING.length) return saved.ranking;
    return DEFAULT_RANKING;
  });

  const total = EXO_DRIVERS.length;
  const [hasSaved, setHasSaved] = useState(() => hasProgress(saved));
  const [inviteLang, setInviteLang] = useState("fr");
  const [entourageToken, setEntourageToken] = useState(() => {
    try {
      const fromPraticien = new URLSearchParams(window.location.search).get("seance");
      if (fromPraticien) localStorage.setItem(ENTOURAGE_KEY, fromPraticien);
      return fromPraticien || localStorage.getItem(ENTOURAGE_KEY) || "";
    } catch { return ""; }
  });
  const [idrRanking, setIdrRanking] = useState(null);

  // Temps 3 : le classement final de l'Inventaire (séance praticien) est repris comme point de départ.
  useEffect(() => {
    if (!entourageToken) return;
    axios.get(`${API}/drivers-entourage/seances/${entourageToken}`).then(({ data }) => {
      const r = data?.idr?.rankings || {};
      const final = r.antidotes || r.dimensions || r.definitions;
      if (!final || final.length !== DEFAULT_RANKING.length) return;
      setIdrRanking(final);
      const rankChanged = saved?.ranking && saved.ranking.join(",") !== DEFAULT_RANKING.join(",");
      if (!rankChanged) setRanking(final);
    }).catch(() => {});
  }, [entourageToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const inviteEntourage = async () => {
    if (entourageToken) {
      await axios
        .post(`${API}/drivers-entourage/seances/${entourageToken}/patient`, { patient_ranking: ranking, patient_entries: entries })
        .catch(() => {});
      setPhase("entourage");
      scrollTop();
      return;
    }
    try {
      const { token } = await axios
        .post(`${API}/drivers-entourage/seances`, { patient_ranking: ranking, patient_entries: entries, lang: inviteLang })
        .then((r) => r.data);
      try { localStorage.setItem(ENTOURAGE_KEY, token); } catch { /* ignore */ }
      setEntourageToken(token);
      setPhase("entourage");
      scrollTop();
    } catch {
      toast.error("Impossible de créer le lien");
    }
  };
  const openEntourage = () => { setPhase("entourage"); scrollTop(); };

  const setEntry = (key, val) => setEntries((prev) => ({ ...prev, [key]: val }));
  const moveRank = (key, dir) =>
    setRanking((prev) => {
      const arr = [...prev];
      const i = arr.indexOf(key);
      const j = i + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });

  useEffect(() => {
    if (phase === "welcome") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ entries, ranking, index }));
    } catch {
      /* mode privé / quota : on ignore */
    }
  }, [entries, ranking, index, phase]);

  const clearSaved = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setHasSaved(false);
  };

  const nextDriver = () => {
    if (index < total - 1) setIndex((i) => i + 1);
    else setPhase("evaluation");
    scrollTop();
  };
  const prevDriver = () => {
    if (index > 0) setIndex((i) => i - 1);
    else setPhase("welcome");
    scrollTop();
  };
  const finish = () => {
    setPhase("synthese");
    axios.post(`${API}/drivers-exo-evaluations`, { entries, ranking }).catch((e) => console.error("save exo failed", e));
    if (entourageToken) {
      axios.post(`${API}/drivers-entourage/seances/${entourageToken}/patient`, { patient_ranking: ranking, patient_entries: entries }).catch(() => {});
    }
    scrollTop();
  };
  const resetState = () => {
    setEntries(freshEntries());
    setRanking(idrRanking || DEFAULT_RANKING);
    setIndex(0);
  };
  const startFresh = () => {
    clearSaved();
    resetState();
    setPhase("drivers");
    scrollTop();
  };
  const resume = () => {
    setIndex(saved?.index || 0);
    setPhase("drivers");
    scrollTop();
  };
  const restart = () => {
    clearSaved();
    resetState();
    setPhase("welcome");
    scrollTop();
  };

  const render = () => {
    if (phase === "welcome") return <Welcome onStartFresh={startFresh} hasSaved={hasSaved} onResume={resume} inviteSlot={<DriversEntourageInvite token={entourageToken} onToken={setEntourageToken} onOpen={openEntourage} />} />;
    if (phase === "drivers") {
      const driver = EXO_DRIVERS[index];
      return <DriverPage driver={driver} index={index} total={total} entry={entries[driver.key]} onChange={(val) => setEntry(driver.key, val)} onPrev={prevDriver} onNext={nextDriver} />;
    }
    if (phase === "evaluation") {
      return <EvaluationStep ranking={ranking} moveRank={moveRank} entries={entries} idrPrefilled={!!idrRanking} onChangeFirstStep={(key, val) => setEntry(key, { ...entries[key], premierPas: val })} onPrev={() => { setPhase("drivers"); setIndex(total - 1); scrollTop(); }} onFinish={finish} />;
    }
    if (phase === "entourage") {
      return <EntouragePanel token={entourageToken} onBack={() => { setPhase("synthese"); scrollTop(); }} />;
    }
    return <Synthese entries={entries} ranking={ranking} onRestart={restart} entourageToken={entourageToken} inviteLang={inviteLang} setInviteLang={setInviteLang} onInvite={inviteEntourage} onOpenEntourage={openEntourage} />;
  };

  return <Layout>{render()}</Layout>;
}
